import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  FormControl,
  FormHelperText,
  Unstable_Grid2 as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';

import type { Community } from '@/types/v2/community';
import type { UnitType } from '@/types/v2/unit-type';
import axios from '@/lib/v2/axios';
// Note: This component renders content only. Parent dialog is responsible for layout.

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time – Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

const createSchema = zod.object({
  name: zod.string().min(1, 'A community name is required'),
  email: zod.string().email('Invalid email address').optional().or(zod.literal('')),
  phone: zod
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number')
    .optional()
    .or(zod.literal('')),
  address: zod.string().optional(),
  address2: zod.string().optional(),
  city: zod.string().optional(),
  state: zod.string().optional(),
  zip_code: zod
    .string()
    .regex(/^\d{5}$/, 'Invalid zip code')
    .optional()
    .or(zod.literal('')),
  timezone: zod.string().optional(),
  unit_types: zod.array(zod.string()).optional(),
});

const editSchema = zod.object({
  name: zod.string().min(1, 'A community name is required'),
  email: zod.string().email('Invalid email address'),
  phone: zod
    .string()
    .min(1, 'A phone number is required')
    .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number'),
  address: zod.string().min(1, 'An address is required'),
  address2: zod.string().optional(),
  city: zod.string().min(1, 'A city is required'),
  state: zod.string().min(1, 'A state is required'),
  zip_code: zod
    .string()
    .min(1, 'A zip code is required')
    .regex(/^\d{5}$/, 'Invalid zip code'),
  timezone: zod.string().optional(),
  unit_types: zod.array(zod.string()).optional(),
});
type CreateValues = zod.infer<typeof createSchema>;
type EditValues = zod.infer<typeof editSchema>;
type Values = CreateValues | EditValues;

interface CreateOrEditCommunityFormProps {
  onSuccess: (data: Community) => void;
  unitTypes: UnitType[];
  initialData?: Values | Community;
  mode: 'create' | 'edit';
  communityId?: string | number;
}



export function CreateOrEditCommunityForm({
  onSuccess,
  unitTypes = [],
  initialData,
  communityId,
  mode,
}: CreateOrEditCommunityFormProps): React.JSX.Element {
  const [error, setError] = React.useState<string | null>(null);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<UnitType[]>([]); // Add state for selected unit types

  const defaultValues = {
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    address2: initialData?.address2 || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zip_code: initialData?.zip_code || '',
    timezone: (initialData as Community)?.timezone || 'America/Chicago',
    unit_types: initialData?.unit_types?.map((unit: any) => unit.id) || [],
  };

  React.useEffect(() => {
    // Initialize selected unit types based on default values
    const initialSelectedUnitTypes = unitTypes.filter((unit) => defaultValues.unit_types.includes(unit.id));
    setSelectedUnitTypes(initialSelectedUnitTypes);
  }, []); // Add unitTypes to dependencies

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(mode === 'create' ? createSchema : editSchema) });

  const createCommunityRequest = useMutation({
    mutationFn: async (data: Values) => {
      if (mode === 'create') {
        await axios.get('/sanctum/csrf-cookie');
        return axios.post('/api/v2/communities', data);
      } 
        if (!communityId) {
          console.error('Community ID is required');
          setError('We encountered an issue while updating the community. Please try again later.');
          return;
        }

        console.log('Updating community', communityId);
        await axios.get('/sanctum/csrf-cookie');
        return axios.put(`/api/v2/communities/${communityId}`, data);
      
    },
  });
  // Show me the current errors so i can troubleshoot
  console.log('Errors', errors);

  const handleCreateOrEditCommunity = useCallback(
    async (data: Values) => {
      data.unit_types = selectedUnitTypes.map((unit) => unit.id); // Update unitTypes with selected ones
      try {
        const response = await createCommunityRequest.mutateAsync(data);
        console.log(response?.data);
        onSuccess(response?.data);
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || `An error occurred while ${mode}ing the community`);
      }
    },
    [createCommunityRequest, selectedUnitTypes, onSuccess, mode] // Add selectedUnitTypes to dependencies
  );



  return (
      <Stack spacing={3}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        
        <form id="create-edit-community-form" onSubmit={handleSubmit(handleCreateOrEditCommunity)}>
          <Grid container spacing={3}>
            {mode === 'edit' && (
              <Grid xs={12}>
                <Typography sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }} variant="h6">Basic Information</Typography>
              </Grid>
            )}
            
            <Grid xs={12}>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.name)} fullWidth>
                    <InputLabel required>Community Name</InputLabel>
                    <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                    {errors.name ? <FormHelperText>{errors.name.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
            </Grid>
            
            {mode === 'edit' && (
              <>
                <Grid md={6} xs={12}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.email)} fullWidth>
                        <InputLabel required>Email</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid md={6} xs={12}>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.phone)} fullWidth>
                        <InputLabel required>Phone</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.phone ? <FormHelperText>{errors.phone.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid xs={12}>
                  <Typography sx={{ mb: 2, mt: 3, color: 'text.primary', fontWeight: 600 }} variant="h6">Location Details</Typography>
                </Grid>

                <Grid md={8} xs={12}>
                  <Controller
                    control={control}
                    name="address"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.address)} fullWidth>
                        <InputLabel required>Address</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.address ? <FormHelperText>{errors.address.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid md={4} xs={12}>
                  <Controller
                    control={control}
                    name="address2"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.address2)} fullWidth>
                        <InputLabel>Address 2 (Optional)</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.address2 ? <FormHelperText>{errors.address2.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid md={4} xs={12}>
                  <Controller
                    control={control}
                    name="zip_code"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.zip_code)} fullWidth>
                        <InputLabel required>Zip Code</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.zip_code ? <FormHelperText>{errors.zip_code.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid md={6} xs={12}>
                  <Controller
                    control={control}
                    name="city"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.city)} fullWidth>
                        <InputLabel required>City</InputLabel>
                        <OutlinedInput disabled={createCommunityRequest.isPending} {...field} />
                        {errors.city ? <FormHelperText>{errors.city.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid md={6} xs={12}>
                  <Controller
                    control={control}
                    name="state"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.state)} fullWidth>
                        <InputLabel required>State</InputLabel>
                        <Select
                          {...field}
                          disabled={createCommunityRequest.isPending}
                          label="State"
                        >
                          <MenuItem value="AL">Alabama</MenuItem>
                          <MenuItem value="AK">Alaska</MenuItem>
                          <MenuItem value="AZ">Arizona</MenuItem>
                          <MenuItem value="AR">Arkansas</MenuItem>
                          <MenuItem value="CA">California</MenuItem>
                          <MenuItem value="CO">Colorado</MenuItem>
                          <MenuItem value="CT">Connecticut</MenuItem>
                          <MenuItem value="DE">Delaware</MenuItem>
                          <MenuItem value="FL">Florida</MenuItem>
                          <MenuItem value="GA">Georgia</MenuItem>
                          <MenuItem value="HI">Hawaii</MenuItem>
                          <MenuItem value="ID">Idaho</MenuItem>
                          <MenuItem value="IL">Illinois</MenuItem>
                          <MenuItem value="IN">Indiana</MenuItem>
                          <MenuItem value="IA">Iowa</MenuItem>
                          <MenuItem value="KS">Kansas</MenuItem>
                          <MenuItem value="KY">Kentucky</MenuItem>
                          <MenuItem value="LA">Louisiana</MenuItem>
                          <MenuItem value="ME">Maine</MenuItem>
                          <MenuItem value="MD">Maryland</MenuItem>
                          <MenuItem value="MA">Massachusetts</MenuItem>
                          <MenuItem value="MI">Michigan</MenuItem>
                          <MenuItem value="MN">Minnesota</MenuItem>
                          <MenuItem value="MS">Mississippi</MenuItem>
                          <MenuItem value="MO">Missouri</MenuItem>
                          <MenuItem value="MT">Montana</MenuItem>
                          <MenuItem value="NE">Nebraska</MenuItem>
                          <MenuItem value="NV">Nevada</MenuItem>
                          <MenuItem value="NH">New Hampshire</MenuItem>
                          <MenuItem value="NJ">New Jersey</MenuItem>
                          <MenuItem value="NM">New Mexico</MenuItem>
                          <MenuItem value="NY">New York</MenuItem>
                          <MenuItem value="NC">North Carolina</MenuItem>
                          <MenuItem value="ND">North Dakota</MenuItem>
                          <MenuItem value="OH">Ohio</MenuItem>
                          <MenuItem value="OK">Oklahoma</MenuItem>
                          <MenuItem value="OR">Oregon</MenuItem>
                          <MenuItem value="PA">Pennsylvania</MenuItem>
                          <MenuItem value="RI">Rhode Island</MenuItem>
                          <MenuItem value="SC">South Carolina</MenuItem>
                          <MenuItem value="SD">South Dakota</MenuItem>
                          <MenuItem value="TN">Tennessee</MenuItem>
                          <MenuItem value="TX">Texas</MenuItem>
                          <MenuItem value="UT">Utah</MenuItem>
                          <MenuItem value="VT">Vermont</MenuItem>
                          <MenuItem value="VA">Virginia</MenuItem>
                          <MenuItem value="WA">Washington</MenuItem>
                          <MenuItem value="WV">West Virginia</MenuItem>
                          <MenuItem value="WI">Wisconsin</MenuItem>
                          <MenuItem value="WY">Wyoming</MenuItem>
                          <MenuItem value="DC">District of Columbia</MenuItem>
                        </Select>
                        {errors.state ? <FormHelperText>{errors.state.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid xs={12}>
                  <Typography sx={{ mb: 2, mt: 3, color: 'text.primary', fontWeight: 600 }} variant="h6">Time Zone</Typography>
                </Grid>

                <Grid md={6} xs={12}>
                  <Controller
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.timezone)} fullWidth>
                        <InputLabel required>Community Timezone</InputLabel>
                        <Select
                          {...field}
                          disabled={createCommunityRequest.isPending}
                          label="Community Timezone"
                        >
                          {US_TIMEZONES.map((tz) => (
                            <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Used to validate amenity availability hours. Choose the timezone where your community is located.
                        </FormHelperText>
                        {errors.timezone ? <FormHelperText error>{errors.timezone.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </Stack>
  );
}
